import type { DealChain } from './dealChain';
import type { ApiDealStepEvent } from 'src/types/dealStep';
import type { ApiDeliveryDeal, ApiDeliveryDealDetail } from 'src/types/delivery';

import { DealStep } from 'src/types/dealStep';

import { formatMoney, formatAmount } from './money';

/**
 * Pure invoice logic for the buyer delivery flow (deal v2 §4.10, contracts v3 §2.1/§4). Kept out
 * of the component so it can be unit-tested in the node env (the app has no RTL setup — only
 * vitest specs for pure helpers).
 *
 * The invoice is two-phase: at offer time the buyer knows price + platform fee, while delivery is
 * quoted by the dealer on Approve (null until then). Amounts are plain numbers in the settlement
 * stablecoin of the listing's currency — USDC, EURC or, on the legacy TRON deals, USDT.
 *
 * **Gas is not a line any more.** v3 settles on Base, where deploying a clone costs cents and the
 * platform absorbs it; new deals are written with a zero gas amount. A non-zero one therefore
 * means one thing only — a TRON deal from before v3 — and it is shown as a footnote in its own
 * currency below the total, never summed into it (two currencies never add up).
 *
 * The on-chain fund() amount is NOT derived here: it is read from the contract's
 * `getTotalRequired()` (see deliveryOnchain.ts) so base-unit precision never depends on
 * client-side decimals.
 */

/** 1 TRX = 1_000_000 SUN (mirrors the api's SUN_PER_TRX used for gas verification). */
export const SUN_PER_TRX = 1_000_000;

export interface DeliveryInvoiceLines {
  /** Car price (USDT). */
  price: number;
  /** Platform fee (USDT). */
  fee: number;
  /** Dealer delivery quote (USDT), or null before Approve. */
  delivery: number | null;
  /** Gas compensation (TRX). */
  gasTrx: number;
}

/** True while the delivery line is still awaiting the dealer's quote (spec §4.10). */
export const isDeliveryQuotePending = (deal: Pick<ApiDeliveryDeal, 'deliveryAmount'>): boolean =>
  deal.deliveryAmount === null || deal.deliveryAmount === undefined;

/** Assemble the invoice lines from a delivery deal. */
export const deliveryInvoiceLines = (
  deal: Pick<ApiDeliveryDeal, 'priceAmount' | 'feeAmount' | 'deliveryAmount' | 'gasTrxAmount'>
): DeliveryInvoiceLines => ({
  price: deal.priceAmount,
  fee: deal.feeAmount,
  delivery: isDeliveryQuotePending(deal) ? null : (deal.deliveryAmount as number),
  gasTrx: Number(deal.gasTrxAmount || '0'),
});

/**
 * The USDT total the buyer funds to the contract = price + delivery + fee. Before the
 * dealer quotes delivery it is a partial sum (delivery treated as 0) — the UI must
 * still show the "Delivery: set by the dealer" line, not a final total.
 */
export const deliveryUsdtTotal = (lines: DeliveryInvoiceLines): number =>
  lines.price + (lines.delivery ?? 0) + lines.fee;

/** The gas the buyer pays to the platform in SUN (native TRX transfer, spec §4.3). */
export const deliveryGasSun = (gasTrx: number): string => String(Math.round(gasTrx * SUN_PER_TRX));

/**
 * Whether this deal still carries the TRON gas compensation (deal v2) or is a v3 deal that does
 * not (§2.1). Read from the amount rather than from the chain: api writes `'0'` for every deal it
 * creates on an EVM chain, so the data already answers the question and no screen has to hold a
 * second opinion about which chain owes gas.
 */
export const hasGasCompensation = (lines: DeliveryInvoiceLines): boolean => lines.gasTrx > 0;

interface InvoiceRow {
  label: string;
  value: string;
}

export interface DeliveryInvoiceView {
  lines: InvoiceRow[];
  total: InvoiceRow;
  /** The legacy TRON gas line, in its own currency, below the total and outside the sum. */
  footnote: InvoiceRow | null;
  /** Moment of payment — the invoice's date. Null until the escrow is funded ("Pending"). */
  issuedAt: string | null;
  /** The transaction that funded the escrow, taken from the deal's own timeline. */
  paymentTxHash: string | null;
}

/**
 * One invoice, assembled once and shown identically on the deal screen and in the payment dialog
 * (contracts v3, UI part §4).
 *
 * Shared on purpose: the two used to build their own rows, and the pay dialog printed a gas line
 * the deal screen did not. Whatever the buyer approves in the wallet must be the number they were
 * shown, and the cheapest way to guarantee that is to have one function produce both.
 *
 * The symbol comes from the deal's chain — the settlement stablecoin of the listing's currency —
 * so a EUR car on Base reads EURC and a legacy TRON deal reads USDT, without any screen holding
 * an opinion about which is which. With no chain resolved yet, amounts are printed bare rather
 * than under a guessed ticker.
 */
export const deliveryInvoiceView = (
  deal: ApiDeliveryDeal & Partial<Pick<ApiDeliveryDealDetail, 'stepEvents'>>,
  chain: DealChain | null
): DeliveryInvoiceView => {
  const lines = deliveryInvoiceLines(deal);
  const symbol = chain?.token?.symbol ?? '';
  const quotePending = isDeliveryQuotePending(deal);
  const amount = (value: number) => (symbol ? formatMoney(value, symbol) : formatAmount(value));

  return {
    lines: [
      { label: 'Car price', value: amount(lines.price) },
      {
        label: 'Delivery',
        value: quotePending ? 'Set by the dealer on approval' : amount(lines.delivery as number),
      },
      { label: 'Platform fee', value: amount(lines.fee) },
    ],
    total: {
      // Before the dealer quotes delivery the sum is not the final one, and calling it "Total"
      // would promise a number that is about to grow.
      label: quotePending ? 'Subtotal (excl. delivery)' : 'Total',
      value: amount(deliveryUsdtTotal(lines)),
    },
    footnote: hasGasCompensation(lines)
      ? { label: 'Gas compensation', value: `${formatAmount(lines.gasTrx)} TRX` }
      : null,
    issuedAt: deal.escrowedAt,
    paymentTxHash: deliveryPaymentTxHash(deal.stepEvents),
  };
};

/** The hash of the transaction that funded the escrow — the receipt's own link. */
export const deliveryPaymentTxHash = (stepEvents: ApiDealStepEvent[] | undefined): string | null =>
  stepEvents?.find((event) => event.step === DealStep.DeliveryEscrowed)?.txHash ?? null;
